import { FlexBox } from '@dixa/lib-shared-habla';
import { Conversation } from 'lib-shared-types-api';

import { ConversationStatusColorBox } from 'components/conversation';
import { ConversationTags } from 'components/conversation/conversationTags';
import { useAppSelector } from 'utils/redux/useAppSelector';

import { ConversationActionBar } from './actionBar/conversationActionBar';
import styles from './conversationHeader.module.scss';
import { ConversationHeadline } from './headline/conversationHeadline';
import { MessengerAuthenticationLevel } from './messengerAuthenticationLevel';
import { ConversationReplyWindow } from './replyWindow/conversationReplyWindow';
import { ConversationToolbar } from './toolbar/conversationToolbar';

/**
 * The top level component for the Conversation Header. This contains components
 * that display information on the conversation and user, and components that
 * can perform actions on the conversation.
 */
export const ConversationHeader = (): JSX.Element => {
  const {
    isFocusMode,
    conversation: { id: csid, attributes, initial_channel, status },
  } = useAppSelector((state) => ({
    conversation: state.conversation.metaConversation.conversation,
    isFocusMode: state.conversation.isFocusMode,
  }));

  const showMessengerAuthenticationLevel = initial_channel === 'messenger';

  const isVerifiedUser =
    attributes?.messenger_created_by_verified_user === 'true';

  return (
    <div className={styles.root}>
      <ConversationStatusColorBox
        csid={csid as Conversation['id']}
        status={status as Conversation['status']}
      />

      <div
        data-testid="conversation-header"
        className={styles.conversationHeader}
      >
        <FlexBox padding="250" flexDirection="column" gap="200">
          <ConversationReplyWindow />
          <FlexBox justifyContent="space-between" flexWrap="wrap-reverse">
            <div>
              <ConversationHeadline isFocusMode={isFocusMode} />
            </div>
            {showMessengerAuthenticationLevel && (
              <MessengerAuthenticationLevel isVerifiedUser={isVerifiedUser} />
            )}
            <div className={styles.topActions}>
              <ConversationToolbar isFocusMode={isFocusMode} />
            </div>
          </FlexBox>
        </FlexBox>

        {!isFocusMode && (
          <FlexBox
            justifyContent="space-between"
            className={styles.tagsAndActions}
          >
            <div className={styles.tags}>
              {csid && <ConversationTags csid={csid} />}
            </div>
            <div className={styles.bottomActions}>
              <ConversationActionBar />
            </div>
          </FlexBox>
        )}
      </div>
    </div>
  );
};
